import { zarazService } from "@/services";
import styled from '@emotion/styled';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import Slider from 'react-slick';
import cx from '@utils/cx.util';

const handleCategoryClick = (item, i) => {
  zarazService.ecommerce.clickedPromotion(item.title, 'Browse By', 'item', i);

  try {
    if (!!window.abtasty) {
      window.abtasty.send('event', {
        ec: 'Action Tracking',
        ea: 'Browse By Click',
        el: item?.title,
      });
    }
  } catch (e) {
    // do nothing
  }
};

const BrowseBy = ({ title, items }) => {
  const sliderRef = useRef<Slider>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const shouldSlide = items.length > 4;

  return (
    <StyledSliderContainer
      className={cx('tw-mx-auto tw-my-6 tw-max-w-[1440px]', {
        'tw-mx-auto lg:tw-px-3 xl:tw-px-6': !shouldSlide,
        'tw-mx-0 xl:tw-mx-6': shouldSlide,
      })}>
      <h2 className='tw-mx-auto tw-mb-6 tw-px-2 tw-font-body tw-text-base tw-font-extrabold tw-uppercase tw-tracking-[1.6px] tw-text-neutrals-graphite md:tw-text-lg xl:tw-text-2xl'>
        {title}
      </h2>

      {!shouldSlide && (
        <div
          className={cx(`tw-hidden tw-gap-6`, {
            'sm:tw-flex': items.length < 3,
            'sm:tw-grid sm:tw-grid-cols-3': items.length === 3,
            'lg:tw-grid lg:tw-grid-cols-4': items.length === 4,
          })}>
          {items.map((item, i) => (
            <BrowseByItem
              key={item.title}
              item={item}
              position={i}
              className={cx({
                '!tw-h-[350px]': items.length < 3,
                '!tw-h-auto': items.length === 3,
              })}
            />
          ))}
        </div>
      )}

      <div className={cx({ 'sm:tw-hidden': items.length < 4, 'lg:tw-hidden': items.length === 4 })}>
        <Slider
          ref={sliderRef}
          slidesToShow={shouldSlide ? 4 : items.length}
          slidesToScroll={1}
          className='tw-mx-0'
          arrows={false}
          dots={false}
          beforeChange={(current, next) => setActiveSlide(next)}
          infinite={true}
          swipe={shouldSlide}
          draggable={shouldSlide}
          adaptiveHeight
          variableWidth
          responsive={[
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 1,
                variableWidth: true,
                draggable: true,
                swipe: true,
                infinite: true,
              },
            },
          ]}>
          {items.map((item, i) => (
            <div key={item.title} className='tw-px-2'>
              <BrowseByItem item={item} position={i} />
            </div>
          ))}
        </Slider>
      </div>

      <div
        className={cx('tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-5', {
          'sm:tw-hidden': items.length < 4,
          'lg:tw-hidden': items.length === 4,
        })}>
        <HiArrowLeft
          size={18}
          className='tw-hidden tw-cursor-pointer lg:tw-block'
          onClick={() => sliderRef.current.slickGoTo(activeSlide === 0 ? items.length - 1 : activeSlide - 1)}
        />

        <div className='tw-flex tw-items-center tw-justify-center tw-gap-2'>
          {items.map((_, idx) => (
            <div
              key={idx}
              className={classNames(
                'tw-h-[8px] tw-w-[8px] tw-cursor-pointer tw-rounded-full tw-border tw-border-black tw-transition tw-duration-300',
                {
                  'tw-bg-transparent hover:tw-bg-black/30': activeSlide !== idx,
                  'tw-bg-black': activeSlide === idx,
                }
              )}
              onClick={() => sliderRef.current.slickGoTo(idx)}></div>
          ))}
        </div>

        <HiArrowRight
          size={18}
          className='tw-hidden tw-cursor-pointer lg:tw-block'
          onClick={() => sliderRef.current.slickGoTo(activeSlide === items.length - 1 ? 0 : activeSlide + 1)}
        />
      </div>
    </StyledSliderContainer>
  );
};

export default BrowseBy;

const StyledSliderContainer = styled.div`
  .slick-dots {
    display: flex !important;
    width: 100%;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px !important;

    li {
      margin: 0px !important;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 7px !important;
      height: 7px !important;

      button {
        width: 7px !important;
        height: 7px !important;
        &::before {
          width: 7px;
          height: 7px;
          background-color: var(--neutrals-light-grey);
        }
      }

      &.slick-active {
        button {
          height: 10px !important;
          width: 10px !important;

          &::before {
            height: 10px;
            width: 10px;
            background-color: var(--neutrals-graphite);
          }
        }
      }
    }
  }
`;

const BrowseByItem = ({ item, position, className = '', containerClassName = '' }) => (
  <a
    href={item.url}
    onClick={(e) => handleCategoryClick(item, position)}
    className={cx('tw-group tw-no-underline hover:tw-no-underline', containerClassName)}>
    <div
      className={cx(
        'tw-mx-auto tw-mb-2 tw-h-[180px] tw-transition tw-duration-200 group-hover:tw-opacity-75 md:tw-h-[250px] lg:tw-h-[280px] xl:tw-h-[335px]',
        className
      )}>
      <img
        src={item.imageUrl}
        alt={item.title}
        className='tw-mr-auto tw-aspect-square tw-h-full tw-max-h-full tw-max-w-full tw-rounded-lg tw-object-cover'
      />
    </div>
    <p className='tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-bold tw-uppercase tw-text-core-deep-navy tw-underline tw-transition tw-duration-200 hover:tw-no-underline group-hover:tw-text-core-flame lg:tw-text-base'>
      {item.title}
    </p>
  </a>
);
